// Workflow resource — TanStack Query hooks + type definitions for
// the /api/workflows/:id HAL endpoint.
//
// The definition is stored as opaque jsonb. The shape mirrors
// girraph.Graph[Node] from the Go workflow engine so definitions
// round-trip between the editor and the lifecycle service.

import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";

import { halFetch, type HalCollection, type HalResource } from "~/lib/hal";

// ── Graph types (mirror girraph.Graph[Node]) ─────────────────────────
// These types match the JSON produced by json.Marshal(girraph.Graph[Node])
// in the Go backend. The workflow engine deserializes this format via
// workflow.WorkflowFromJSON.

export type NodeType = "module" | "task" | "trigger";
export type State = "todo" | "in progress" | "complete" | "rejected" | "delayed" | "cancelled" | "error";

export interface Target {
  type: string;
  /** Type-specific identifier. For `role`: the role name string. For
   *  `system`: empty string. For `user`/subject: a UUID. Named `key`
   *  rather than `id` because the value isn't UUID-shaped for the
   *  role/system cases. Mirrors `core.Target.Key` in the Go API. */
  key: string;
}

/** Sentinel for `Target.type` indicating a role-routed reference —
 *  Target.key is the role string, and the spawn services on the API
 *  side translate it into a case_users lookup at task-row creation.
 *  Mirror of `workflow.TargetTypeRole` in the Go API. After TMF-152
 *  every assignee is role-typed; the system identity lives entirely
 *  in `Target.key === SYSTEM_ASSIGNEE_LABEL`. */
export const TARGET_TYPE_ROLE = "role";

/** The string surfaced in the role/assignee dropdown for system-routed
 *  work AND the canonical `Target.key` for those assignees on the
 *  wire — `{type: "role", key: "System"}`. The API spawn flow pins
 *  matched tasks to the singleton system user (no case_users lookup).
 *  Mirror of `enums.RoleSystem` in the Go API. Kept distinct from
 *  `TASK_ROLES` so a real firm role can never collide. */
export const SYSTEM_ASSIGNEE_LABEL = "System";

export interface TaskMeta {
  type: string;
  subject?: Target;
  /**
   * Routing intent. The firm's workflows always emit
   * `assignee = {type: "role", key: <role>}`; the spawn services
   * translate that to a case_users lookup at task-row creation
   * time. Direct-user assignment via `{type: "user", key: <uuid>}`
   * is supported by the shape but unused in production today.
   */
  assignee?: Target;
  // Editor-only field stored alongside the Go Task fields.
  // The Go side ignores unknown JSON fields during deserialization,
  // so this travels safely in the jsonb definition.
  due_day_offset?: number;
  /**
   * Completion mode. true = manually marked complete (the case-detail
   * "Mark done" button); false = implicitly completed via mutation
   * of some other resource (e.g. logging an expense, attaching a
   * record). Required: every TaskMeta produced by the editor (or
   * stored in the database after the fix-everything migration) sets
   * it explicitly; legacy data has been backfilled.
   */
  is_manual: boolean;
}

export interface ModuleMeta {
  type: string;
  subject?: Target;
  assignee?: Target;
}

export interface Condition {
  task: TaskMeta;
  state: State;
  by?: Target;
}

export interface ModuleCondition {
  module: ModuleMeta;
  state: State;
  by?: Target;
}

export interface TriggerMeta {
  type: string;
  conditions: Condition[];
  module_conditions: ModuleCondition[];
  repeatable: boolean;
}

export interface NodeMeta {
  type: NodeType;
  name: string;
  task?: TaskMeta;
  module?: ModuleMeta;
  trigger?: TriggerMeta;
}

/** A node in the workflow graph — mirrors girraph.Graph[Node] JSON. */
export interface GraphNode {
  ID: string;
  Meta: NodeMeta;
  Children: GraphNode[];
}

/** The workflow definition stored in the DB — a serialized girraph graph. */
export type WorkflowDefinition = GraphNode;

// ── Legacy flat types (for migration/conversion) ─────────────────────

export interface LegacyWorkflowTask {
  id: string;
  name: string;
  role: string;
  dueDayOffset: number;
  children?: LegacyWorkflowTask[];
}

export interface LegacyWorkflowPhase {
  id: string;
  name: string;
  tasks: LegacyWorkflowTask[];
  children?: LegacyWorkflowPhase[];
}

export interface LegacyWorkflowDefinition {
  phases: LegacyWorkflowPhase[];
}

/** Type guard: is this definition in the legacy flat format? */
export function isLegacyDefinition(def: unknown): def is LegacyWorkflowDefinition {
  return def != null && typeof def === "object" && "phases" in def && Array.isArray((def as LegacyWorkflowDefinition).phases);
}

/** Type guard: is this definition in the graph format? */
export function isGraphDefinition(def: unknown): def is GraphNode {
  return def != null && typeof def === "object" && "ID" in def && "Meta" in def && "Children" in def;
}

// ── Graph helpers ────────────────────────────────────────────────────

/** Create a new graph node with a random UUID. */
export function makeNode(meta: NodeMeta, children: GraphNode[] = []): GraphNode {
  return { ID: crypto.randomUUID(), Meta: meta, Children: children };
}

/** Create a module (phase) node. */
export function makeModuleNode(name: string, children: GraphNode[] = []): GraphNode {
  return makeNode({ type: "module", name, module: { type: name } }, children);
}

/** Create a task node. Every assignee is role-typed since TMF-152;
 *  the `role` argument is just the Assignee.key:
 *  - "" (default): department-pool / unrouted task; the API spawn flow
 *    leaves user_id NULL until someone claims it.
 *  - SYSTEM_ASSIGNEE_LABEL ("System"): pinned to the singleton system
 *    user by the API spawn flow; no case_users lookup.
 *  - any other non-empty string: looked up via case_users for that
 *    firm role.
 */
export function makeTaskNode(
  name: string,
  role: string = "",
  dueDayOffset: number = 0,
  isManual: boolean = true,
): GraphNode {
  const task: TaskMeta = {
    type: name,
    due_day_offset: dueDayOffset,
    is_manual: isManual,
    assignee: { type: TARGET_TYPE_ROLE, key: role },
  };
  return makeNode({ type: "task", name, task }, []);
}

/** Create a trigger node with module conditions. */
export function makeTriggerNode(
  name: string,
  moduleConditions: ModuleCondition[],
  children: GraphNode[],
  repeatable: boolean = false,
): GraphNode {
  return makeNode(
    {
      type: "trigger",
      name,
      trigger: { type: name, conditions: [], module_conditions: moduleConditions, repeatable },
    },
    children,
  );
}

/** Find all direct child nodes of a specific type. */
export function childrenOfType(node: GraphNode, type: NodeType): GraphNode[] {
  return (node.Children ?? []).filter((c) => c.Meta.type === type);
}

/**
 * Extract the "phase view" from a graph — returns the list of module nodes
 * (phases) in the order they appear as root children or as trigger targets,
 * along with the trigger that gates each one (null for the first ungated phase).
 */
export interface PhaseView {
  module: GraphNode;
  trigger: GraphNode | null;
}

export function extractPhases(root: GraphNode): PhaseView[] {
  const phases: PhaseView[] = [];
  const seen = new Set<string>();
  for (const child of root.Children ?? []) {
    if (child.Meta.type === "module") {
      if (!seen.has(child.ID)) {
        seen.add(child.ID);
        phases.push({ module: child, trigger: null });
      }
    } else if (child.Meta.type === "trigger") {
      for (const target of child.Children ?? []) {
        if (target.Meta.type === "module" && !seen.has(target.ID)) {
          seen.add(target.ID);
          phases.push({ module: target, trigger: child });
        }
      }
    }
  }
  return phases;
}

/** Find a node by ID anywhere in the graph (depth-first). */
export function findNodeByID(root: GraphNode, id: string): GraphNode | null {
  if (root.ID === id) return root;
  for (const child of root.Children ?? []) {
    const found = findNodeByID(child, id);
    if (found) return found;
  }
  return null;
}

/** Replace a node by ID anywhere in the graph (returns a new tree). */
export function replaceNode(root: GraphNode, id: string, replacement: GraphNode): GraphNode {
  if (root.ID === id) return replacement;
  return {
    ...root,
    Children: (root.Children ?? []).map((c) => replaceNode(c, id, replacement)),
  };
}

/** Remove a node by ID anywhere in the graph (returns a new tree). */
export function removeNode(root: GraphNode, id: string): GraphNode {
  return {
    ...root,
    Children: (root.Children ?? [])
      .filter((c) => c.ID !== id)
      .map((c) => removeNode(c, id)),
  };
}

/** Create an empty root workflow graph. */
export function makeEmptyWorkflow(): GraphNode {
  return makeModuleNode("Case Lifecycle");
}

// ── Task roles ────────────────────────────────────────────────────────
//
// Hardcoded until the firm's role catalog becomes a real API resource.
// Exported here so both editors (graph and list) import from one
// place — keeping two copies in sync by hand was a bug surface.

export const TASK_ROLES = [
  "Associate Attorney",
  "Bills and Records",
  "Case Manager",
  "Claims Manager",
  "Client Success Team",
  "Finance",
  "Intake Specialist",
  "Lead Case Manager",
  "Legal Assistant",
  "Legal Secretary",
  "Litigation Paralegal",
  "Managing Partner",
  "NCD",
  "Negotiator",
  "New Client Development",
  "Paralegal",
  "Primary",
  "Records",
  "Support",
] as const;

// ── Editor view model ────────────────────────────────────────────────
//
// The list editor operates on a simplified "view model" that mirrors
// the old flat format (phases with tasks). `graphToEditorModel` and
// `editorModelToGraph` are pure round-trip converters used at the
// component boundary — the graph editor works directly on `GraphNode`,
// while the list editor converts on mount and serializes on change.
//
// Round-trip preserves node IDs so the graph editor and the list
// editor see the same nodes after switching modes.
//
// ⚠ Round-trip CURRENTLY preserves only the fields the list view
// exposes: node ID, name, task role + due-day offset, trigger conditions
// + repeatable. Any extra `Meta.task` / `Meta.module` / `Meta.trigger`
// field (e.g. `subject`, `assignee`, or future task-fidelity fields
// from TMF-32 like `startDayOffset`, `notifyRoles`) would be dropped
// when passing through these converters. Today nothing populates those
// extras (the Filevine seed script strips `_fv*` fields before sending,
// and the engine ignores them), so this is a latent issue, not an
// active one. When TMF-32's fields land, extend EditorTask/EditorPhase
// alongside the converter so nothing gets silently lost.

export interface EditorTask {
  id: string;
  name: string;
  role: string;
  dueDayOffset: number;
  // Preserved through the list-editor round-trip even though the
  // list editor doesn't expose a control for it — graph-editor users
  // who set is_manual shouldn't lose it just because someone else
  // later opens the workflow in the list view.
  isManual: boolean;
  children: EditorTask[];
}

export interface EditorPhase {
  /** The module GraphNode's ID. */
  id: string;
  name: string;
  tasks: EditorTask[];
  trigger: {
    /** The trigger GraphNode's ID. */
    id: string;
    name: string;
    moduleConditions: ModuleCondition[];
    repeatable: boolean;
  } | null;
}

export interface EditorModel {
  rootId: string;
  rootName: string;
  phases: EditorPhase[];
}

/** Convert a graph definition to the editor view model. Handles the
 *  legacy flat format too, so old workflows don't crash the editor. */
export function graphToEditorModel(def: GraphNode): EditorModel {
  // Legacy flat format ({ phases: [...] }) — kept for graceful upgrade
  // of definitions that predate the graph format.
  if (isLegacyDefinition(def)) {
    const legacy = def as unknown as LegacyWorkflowDefinition;
    return {
      rootId: crypto.randomUUID(),
      rootName: "Workflow",
      phases: legacy.phases.map((p) => ({
        id: p.id,
        name: p.name,
        tasks: legacyTasksToEditorTasks(p.tasks),
        trigger: null,
      })),
    };
  }

  const phases = extractPhases(def);
  return {
    rootId: def.ID,
    rootName: def.Meta.name,
    phases: phases.map(({ module, trigger }) => ({
      id: module.ID,
      name: module.Meta.name,
      tasks: graphTasksToEditorTasks(childrenOfType(module, "task")),
      trigger: trigger
        ? {
            id: trigger.ID,
            name: trigger.Meta.name,
            moduleConditions: trigger.Meta.trigger?.module_conditions ?? [],
            repeatable: trigger.Meta.trigger?.repeatable ?? false,
          }
        : null,
    })),
  };
}

function legacyTasksToEditorTasks(tasks: LegacyWorkflowTask[]): EditorTask[] {
  return tasks.map((t) => ({
    id: t.id,
    name: t.name,
    role: t.role,
    dueDayOffset: t.dueDayOffset,
    // Legacy definitions predate is_manual; default to manual since
    // that mirrors the historic "external_logged was the only wired
    // completion mode" behavior.
    isManual: true,
    children: legacyTasksToEditorTasks(t.children ?? []),
  }));
}

function graphTasksToEditorTasks(nodes: GraphNode[]): EditorTask[] {
  return nodes.map((n) => {
    // Every assignee is role-typed since TMF-152:
    //   {type:"role", key:""}        → department-pool / unrouted
    //   {type:"role", key:"System"}  → system user (the system role)
    //   {type:"role", key:<role>}    → firm-role string
    //   absent / other                → "" (no assignee)
    const assignee = n.Meta.task?.assignee;
    let role = "";
    if (assignee && assignee.type === TARGET_TYPE_ROLE) {
      role = assignee.key;
    }
    return {
      id: n.ID,
      name: n.Meta.name,
      role,
      dueDayOffset: n.Meta.task?.due_day_offset ?? 0,
      // is_manual is required on TaskMeta now; the ?? false here is
      // for the structurally-impossible case where a task-typed node
      // has no Meta.task at all (childrenOfType filters by type, so
      // it shouldn't happen). False is the safer default for broken
      // data — no "Mark done" button shown on something we can't
      // identify as manual.
      isManual: n.Meta.task?.is_manual ?? false,
      children: graphTasksToEditorTasks(childrenOfType(n, "task")),
    };
  });
}

/** Convert the editor view model back to a graph definition. Preserves
 *  node IDs so round-tripping through both editors is stable. */
export function editorModelToGraph(model: EditorModel): GraphNode {
  const children: GraphNode[] = [];

  for (const phase of model.phases) {
    const moduleNode = makeModuleNode(phase.name, editorTasksToGraphNodes(phase.tasks));
    moduleNode.ID = phase.id;

    if (phase.trigger) {
      const triggerNode = makeTriggerNode(
        phase.trigger.name,
        phase.trigger.moduleConditions,
        [moduleNode],
        phase.trigger.repeatable,
      );
      triggerNode.ID = phase.trigger.id;
      children.push(triggerNode);
    } else {
      children.push(moduleNode);
    }
  }

  return {
    ID: model.rootId,
    Meta: { type: "module", name: model.rootName, module: { type: model.rootName } },
    Children: children,
  };
}

function editorTasksToGraphNodes(tasks: EditorTask[]): GraphNode[] {
  return tasks.map((t) => {
    const node = makeTaskNode(t.name, t.role, t.dueDayOffset, t.isManual);
    node.ID = t.id;
    node.Children = editorTasksToGraphNodes(t.children);
    return node;
  });
}

/** Slim per-case reference for the danger-zone "still in use" list. The
 *  workflow_id FK moved onto the project supertype (TMF-170), so the API
 *  embeds projects (not cases); this is the derived view model, id + name for
 *  the case link, produced from each project's nested case. */
export interface WorkflowCaseRef {
  id: string;
  name: string;
}

/** A project (workflow instance) embedded on a workflow detail response,
 *  carrying (nested) the case it maps to, which is what names it. */
export interface WorkflowProjectRef {
  id: string;
  _embedded?: {
    case?: { id: string; name: string };
  };
}

/** The shape goat embeds on a Workflow when relations are loaded.
 *  Only populated on View (`GET /api/workflows/:id`); the List
 *  endpoint omits _embedded so it stays slim. */
export interface WorkflowEmbedded {
  projects?: WorkflowProjectRef[];
}

/**
 * The Workflow resource returned from GET /api/workflows/:id,
 * typed as a HalResource so callers get the _links envelope for
 * free when they follow links.
 */
export type Workflow = HalResource<{
  id: string;
  name: string;
  definition: WorkflowDefinition;
  created_at: string;
  updated_at: string;
  _embedded?: WorkflowEmbedded;
}>;

// ── HAL collection type ────────────────────────────────────────────────

export type WorkflowCollection = HalCollection<Workflow>;

// ── Queries ───────────────────────────────────────────────────────────

export const WORKFLOWS_LIST_KEY = ["workflows"] as const;

export const workflowsListQueryOptions = queryOptions<WorkflowCollection>({
  queryKey: WORKFLOWS_LIST_KEY,
  queryFn: ({ signal }) =>
    halFetch<WorkflowCollection>("/api/workflows", { signal }),
});

export const workflowQueryKey = (id: string) =>
  ["workflows", id] as const;

export const workflowQueryOptions = (id: string) =>
  queryOptions<Workflow>({
    queryKey: workflowQueryKey(id),
    queryFn: ({ signal }) =>
      halFetch<Workflow>(`/api/workflows/${id}`, { signal }),
  });

// ── Mutations ─────────────────────────────────────────────────────────

interface CreateWorkflowVariables {
  name: string;
  definition: WorkflowDefinition;
}

export function useCreateWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: CreateWorkflowVariables) =>
      halFetch<Workflow>("/api/workflows", {
        method: "POST",
        body: variables,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WORKFLOWS_LIST_KEY });
    },
  });
}

export function useDeleteWorkflow() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      halFetch<null>(`/api/workflows/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: WORKFLOWS_LIST_KEY });
    },
  });
}

interface UpdateWorkflowVariables {
  id: string;
  definition: WorkflowDefinition;
}

/**
 * PUT /api/workflows/:id with a new definition. The backend's partial
 * PUT semantics mean sending only `definition` leaves `name` alone
 * (verified in the e2e suite, e2e/tests/workflows.spec.ts), so this
 * mutation deliberately does NOT send the name field.
 *
 * On success, writes the server response back into the query cache
 * so any component reading the same query key sees fresh data
 * without an extra round trip. Callers that want optimistic updates
 * can wrap this mutation in their own useMutation with onMutate.
 */
export function useUpdateWorkflowDefinition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, definition }: UpdateWorkflowVariables) =>
      halFetch<Workflow>(`/api/workflows/${id}`, {
        method: "PUT",
        body: { definition },
      }),
    onSuccess: (updated, variables) => {
      queryClient.setQueryData<Workflow>(
        workflowQueryKey(variables.id),
        updated,
      );
    },
  });
}

interface UpdateWorkflowNameVariables {
  id: string;
  name: string;
}

/**
 * PUT /api/workflows/:id with a new name only. Separate from
 * useUpdateWorkflowDefinition because the edit page's name field is
 * explicit-save (a small form with a Save button), while definition
 * edits flow through the editor's debounced autosave. Keeping the two
 * mutations apart lets each onSuccess update only its own slice of
 * the workflow without races between explicit + debounced writes.
 */
export function useUpdateWorkflowName() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, name }: UpdateWorkflowNameVariables) =>
      halFetch<Workflow>(`/api/workflows/${id}`, {
        method: "PUT",
        body: { name },
      }),
    onSuccess: (updated, variables) => {
      queryClient.setQueryData<Workflow>(
        workflowQueryKey(variables.id),
        updated,
      );
      // Name shows up in the list view too — invalidate so the list
      // refreshes next time it's read.
      queryClient.invalidateQueries({ queryKey: WORKFLOWS_LIST_KEY });
    },
  });
}
